import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { useRole } from '@/context/RoleContext';
import { formatMoney } from '@/utils/format';
import { api, ApiError } from '@/utils/api';
import type { Invoice } from '@/types/portal';
import InvoiceHeader from './components/InvoiceHeader';
import InvoiceBreakdown from './components/InvoiceBreakdown';
import InvoiceValidation from './components/InvoiceValidation';
import InvoiceTrail from './components/InvoiceTrail';
import InvoiceDecisionPanel from './components/InvoiceDecisionPanel';
import EarlyPayoutNotice from '@/components/feature/EarlyPayoutNotice';


type Decision = 'approve' | 'reject' | 'hold';

const decisionLabels: Record<Decision, { title: string; status: string; verb: string; icon: string }> = {
  approve: { title: 'Approve this invoice', status: 'Approved', verb: 'approved', icon: 'ri-check-line' },
  reject: { title: 'Reject this invoice', status: 'Rejected', verb: 'rejected', icon: 'ri-close-line' },
  hold: { title: 'Put this invoice on hold', status: 'On hold', verb: 'placed on hold', icon: 'ri-pause-circle-line' },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useRole();
  const canApprove = can('invoices.approve');

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [decision, setDecision] = useState<Decision | null>(null);
  const [saving, setSaving] = useState(false);
  const status = invoice?.status ?? '';

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    api<Invoice>(`/invoices/${id}`)
      .then((data) => {
        if (cancelled) return;
        setInvoice(data);
        setLoadState('ready');
      })
      .catch((error: ApiError) => {
        if (cancelled) return;
        if (error.status === 404) {
          setInvoice(null);
          setLoadState('ready');
        } else {
          setLoadError(error.message);
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loadState === 'loading') {
    return (
      <div className="flex items-center justify-center px-4 py-24 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin" />
        Loading invoice…
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="rounded-lg border border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-800">
          <i className="ri-error-warning-line mr-1" />
          {loadError}
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
            <i className="ri-file-search-line" />
          </span>
          <h1 className="mt-3 font-heading text-lg font-semibold text-foreground-950">
            Invoice not found
          </h1>
          <p className="mt-1 max-w-sm text-sm text-foreground-600">
            This invoice may have been removed or the link is out of date.
          </p>
          <div className="mt-4">
            <Button variant="outline" icon="ri-arrow-left-line" onClick={() => navigate('/invoices')}>
              Back to invoices
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // InvoiceController::approve / reject; "hold" has no backend equivalent yet and is refused.
  const applyDecision = async () => {
    if (!decision) return;
    const config = decisionLabels[decision];
    setSaving(true);
    setActionError('');
    try {
      const updated = await api<Invoice>(`/invoices/${invoice.id}/${decision}`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
      setInvoice(updated);
      setNotice(`Invoice ${invoice.number} ${config.verb}.`);
      setDecision(null);
      setNote('');
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save the decision.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/invoices" className="cursor-pointer hover:text-foreground-800">
          Invoices
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{invoice.number}</span>
      </nav>

      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
        <Link
          to={`/creators/${invoice.creatorId}`}
          className="inline-flex items-center gap-1 cursor-pointer hover:text-foreground-800"
        >
          <i className="ri-user-line" />
          Creator profile
        </Link>
        <Link
          to={`/campaigns/${invoice.campaignId}`}
          className="inline-flex items-center gap-1 cursor-pointer hover:text-foreground-800"
        >
          <i className="ri-megaphone-line" />
          {invoice.campaign}
        </Link>
      </div>

      <div className="mt-4">
        <InvoiceHeader
          invoice={invoice}
          status={status}
          canApprove={canApprove}
          onReview={() => setDecision('approve')}
          onHold={() => setDecision('hold')}
        />
      </div>

      {invoice.advance ? (
        <div className="mt-4">
          <EarlyPayoutNotice
            advance={invoice.advance}
            currency={invoice.currency}
            invoiceTotal={invoice.amount}
          />
        </div>
      ) : null}

      {notice ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <InvoiceBreakdown invoice={invoice} />
          <InvoiceValidation invoice={invoice} />
          <InvoiceTrail invoice={invoice} />
        </div>
        <div>
          <InvoiceDecisionPanel
            invoice={invoice}
            status={status}
            canApprove={canApprove}
            note={note}
            onNoteChange={setNote}
            onApprove={() => setDecision('approve')}
            onReject={() => setDecision('reject')}
            onHold={() => setDecision('hold')}
          />
        </div>
      </div>

      <Modal
        open={decision !== null}
        onClose={() => {
          setDecision(null);
          setActionError('');
        }}
        icon={decision ? decisionLabels[decision].icon : undefined}
        title={decision ? decisionLabels[decision].title : ''}
        subtitle={`${invoice.number} · ${invoice.creatorName} · ${formatMoney(invoice.amount, invoice.currency)}`}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDecision(null);
                setActionError('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant={decision === 'reject' ? 'accent' : 'primary'}
              icon={decision ? decisionLabels[decision].icon : undefined}
              onClick={() => void applyDecision()}
              disabled={saving || (decision === 'reject' && !note.trim())}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          {decision === 'reject'
            ? 'The creator will be notified and the invoice returned for correction. Add a reason below.'
            : decision === 'hold'
              ? 'Holding pauses approval until the blocking checks are resolved.'
              : 'Approved invoices are queued for the next payout batch and settled on the assigned rail.'}
        </p>

        {actionError ? (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5" />
            <span>{actionError}</span>
          </div>
        ) : null}

        {decision === 'approve' && invoice.checks.some((check) => check.status === 'fail') ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            <span>This invoice still has a blocking check. Review it in detail before approving.</span>
          </div>
        ) : null}

        <div className="mt-4">
          <label
            htmlFor="invoice-confirm-note"
            className="text-xs font-label font-medium text-foreground-600"
          >
            {decision === 'reject' ? 'Reason for rejection (required)' : 'Note (optional)'}
          </label>
          <textarea
            id="invoice-confirm-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Add context for the audit trail..."
            className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <div className="mt-1 flex items-center justify-between text-[11px]">
            <span className="text-primary-700">
              {decision === 'reject' && !note.trim() ? 'A reason is required to reject an invoice.' : ''}
            </span>
            <span className="text-foreground-400">{note.length}/500</span>
          </div>
        </div>
      </Modal>
    </div>
  );
}